import { PERSON_FIELDS, type PersonField } from './rules';
import { missingOf, type PersonResult, type RecordResult } from './people';

/** Everything the "who is incomplete" modal and its spreadsheet need, one row per person. */

export type IncompleteKey = PersonField | 'location' | 'lookupFailed';
export type IncompleteWho = 'Person A' | 'Person B' | 'Whole record';

export interface IncompleteRow {
  /** requestID / intakeID / caseID / registrationID, depending on the source. */
  id: number;
  reference: string;
  who: IncompleteWho;
  /** What we do know about them (empty string when unknown), so the user can recognise them. */
  firstName: string;
  surname: string;
  email: string;
  phone: string;
  gender: string;
  dateOfBirth: string;
  /** The date of birth exactly as received when it was present but unusable; empty otherwise. */
  dobReceived: string;
  location: string;
  /** Best way to recognise the person: full name, else email, else phone, else "Unknown person". */
  label: string;
  /** Machine keys, used by the modal's "missing field" filter. */
  keys: IncompleteKey[];
  /** Human labels, e.g. ["First name", "Surname", "Date of birth (invalid date)"]. */
  missing: string[];
  /** `missing` joined for the CSV. */
  missingText: string;
}

const LABEL: Record<IncompleteKey, string> = {
  firstName: 'First name',
  surname: 'Surname',
  email: 'Email',
  phone: 'Phone',
  gender: 'Gender',
  dateOfBirth: 'Date of birth',
  location: 'Location',
  lookupFailed: 'Detail lookup failed',
};

/** Filter choices for the modal, in the same order as the columns. */
export const INCOMPLETE_FILTER_OPTIONS: { value: IncompleteKey; label: string }[] = [
  ...PERSON_FIELDS.map((f) => ({ value: f as IncompleteKey, label: LABEL[f] })),
  { value: 'location', label: LABEL.location },
  { value: 'lookupFailed', label: LABEL.lookupFailed },
];

const text = (v: string | null | undefined) => v ?? '';

function personRow(r: RecordResult, who: 'Person A' | 'Person B', p: PersonResult): IncompleteRow | null {
  const miss = missingOf(p);
  if (miss.length === 0) return null;

  const firstName = text(p.values.firstName);
  const surname = text(p.values.surname);
  const email = text(p.values.email);
  const phone = text(p.values.phone);
  const missing = miss.map((m) =>
    m.field === 'location' ? LABEL.location
      : m.issue ? `${LABEL[m.field]} (${m.issue} date)` : LABEL[m.field]);

  return {
    id: r.id, reference: r.reference, who,
    firstName, surname, email, phone,
    gender: text(p.values.gender),
    dateOfBirth: text(p.values.dateOfBirth),
    dobReceived: text(p.rawIssues?.dateOfBirth),
    location: text(r.location),
    label: [firstName, surname].filter(Boolean).join(' ') || email || phone || 'Unknown person',
    keys: miss.map((m) => m.field),
    missing,
    missingText: missing.join(', '),
  };
}

const recordRow = (r: RecordResult, key: 'location' | 'lookupFailed'): IncompleteRow => ({
  id: r.id, reference: r.reference, who: 'Whole record',
  firstName: '', surname: '', email: '', phone: '', gender: '', dateOfBirth: '', dobReceived: '',
  location: text(r.location),
  label: r.reference,
  keys: [key],
  missing: [LABEL[key]],
  missingText: LABEL[key],
});

/**
 * One row per person who is still missing something after every lookup, plus one row for a record
 * whose detail call failed or whose location is empty (a record-level gap, not a person's).
 * Same "missing" rule as the Missing fields column, because it reads the same `missingOf`.
 */
export function buildIncomplete(results: RecordResult[]): IncompleteRow[] {
  const rows: IncompleteRow[] = [];
  for (const r of results) {
    if (r.failed) { rows.push(recordRow(r, 'lookupFailed')); continue; }
    const a = personRow(r, 'Person A', r.a);
    if (a) rows.push(a);
    if (r.b) {
      const b = personRow(r, 'Person B', r.b);
      if (b) rows.push(b);
    }
    if (r.requireLocation && !r.location) rows.push(recordRow(r, 'location'));
  }
  return rows;
}
